import { useState } from 'react';
import Modal from './Modal';
import { useTranslation } from '../i18n';
import './DataMergeModal.scss';

/**
 * DataMergeModal - shown when authenticated user has existing local data
 * Offers options to merge or discard local data
 */
function DataMergeModal({ 
  isOpen, 
  onClose, 
  localData, 
  onMerge, 
  onDiscard
}) {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Count local items
  const projectCount = localData?.projects?.filter(p => !p.isSystem)?.length || 0;
  const eventCount = localData?.events?.length || 0;
  const milestoneCount = localData?.milestones?.length || 0;
  const totalItems = projectCount + eventCount + milestoneCount;

  const handleMerge = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onMerge();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDiscard = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onDiscard();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen || totalItems === 0) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('dataMerge.title')}>
      <div className="data-merge">
        <p className="data-merge__description">
          {t('dataMerge.description')}
        </p>

        <div className="data-merge__summary">
          <h4>{t('dataMerge.localData')}</h4>
          <ul>
            {projectCount > 0 && (
              <li>{t('dataMerge.projects', { count: projectCount })}</li>
            )}
            {eventCount > 0 && (
              <li>{t('dataMerge.events', { count: eventCount })}</li>
            )}
            {milestoneCount > 0 && (
              <li>{t('dataMerge.milestones', { count: milestoneCount })}</li>
            )}
          </ul>
        </div>

        {error && (
          <div className="data-merge__error">{error}</div>
        )}

        <div className="data-merge__options">
          <button
            className="data-merge__option data-merge__option--primary"
            onClick={handleMerge}
            disabled={isProcessing}
          >
            <span className="data-merge__option-icon">+</span>
            <div className="data-merge__option-content">
              <span className="data-merge__option-title">{t('dataMerge.mergeOption')}</span>
              <span className="data-merge__option-desc">{t('dataMerge.mergeDescription')}</span>
            </div>
          </button>

          <button
            className="data-merge__option"
            onClick={handleDiscard}
            disabled={isProcessing}
          >
            <span className="data-merge__option-icon">x</span>
            <div className="data-merge__option-content">
              <span className="data-merge__option-title">{t('dataMerge.discardOption')}</span>
              <span className="data-merge__option-desc">{t('dataMerge.discardDescription')}</span>
            </div>
          </button>

        </div>

        {isProcessing && (
          <div className="data-merge__processing">
            {t('dataMerge.processing')}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default DataMergeModal;
